import { h } from 'preact';
import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { timeAgo } from '../utils/modelHelpers';

interface Presentation {
  name: string;
  path: string;
  relativePath: string;
  timestamp: number;
  hasPng: boolean;
}

interface ProjectRoadmap {
  projectKey: string;
  roadmapPath: string | null;
  roadmapRelPath: string | null;
  presentations: Presentation[];
  lastUpdated: string | null;
}

function PresentationCard({ pres }: { pres: Presentation }) {
  const openFile = (e: MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: pres.relativePath } }));
  };

  return (
    <div class="roads-pres-card" onClick={openFile}>
      <div class="roads-pres-icon">🎯</div>
      <div class="roads-pres-info">
        <div class="roads-pres-name">{pres.name}</div>
        <div class="roads-pres-meta">
          HTML deck{pres.hasPng ? ' · screenshot' : ''} · {timeAgo(new Date(pres.timestamp).toISOString())}
        </div>
      </div>
      <button class="roads-pres-open" onClick={openFile} title="Open presentation">↗</button>
      <button class="roads-pres-share" onClick={(e: MouseEvent) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('valko-share', {
          detail: { type: 'file', filePath: pres.relativePath, fileName: pres.name, pdfCapable: pres.name.endsWith('.html') || pres.name.endsWith('.htm') },
        }));
      }} title="Share">↗</button>
    </div>
  );
}

interface RoadmapCardProps {
  roadmap: ProjectRoadmap;
}

function RoadmapCard({ roadmap }: RoadmapCardProps) {
  const [expanded, setExpanded] = useState(false);

  const openRoadmap = (e: MouseEvent) => {
    e.stopPropagation();
    if (roadmap.roadmapRelPath) {
      window.dispatchEvent(new CustomEvent('open-file-viewer', { detail: { path: roadmap.roadmapRelPath } }));
    }
  };

  const generatePresentation = (e: MouseEvent) => {
    e.stopPropagation();
    window.dispatchEvent(new CustomEvent('valko-generate-roadmap-presentation', {
      detail: { projectKey: roadmap.projectKey, roadmapPath: roadmap.roadmapRelPath },
    }));
  };

  const hasPresentations = roadmap.presentations.length > 0;
  const hasRoadmap = !!roadmap.roadmapPath;

  return (
    <div class={`roads-card${expanded ? ' roads-card--expanded' : ''}`}>
      <div class="roads-card-header" onClick={() => setExpanded(!expanded)}>
        <div class="roads-card-top">
          <span class="roads-card-chevron">{expanded ? '▼' : '▶'}</span>
          <span class="roads-card-name">{roadmap.projectKey}</span>
          <div class="roads-card-badges">
            {hasRoadmap && <span class="roads-badge roads-badge--map">📍 Roadmap</span>}
            {hasPresentations && (
              <span class="roads-badge roads-badge--deck">
                🎯 {roadmap.presentations.length} deck{roadmap.presentations.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        {hasRoadmap && roadmap.lastUpdated && (
          <div class="roads-card-meta">Updated {timeAgo(roadmap.lastUpdated)}</div>
        )}
      </div>

      {expanded && (
        <div class="roads-card-body">
          {/* Actions */}
          <div class="roads-card-actions">
            {hasRoadmap && (
              <button class="roads-action-btn" onClick={openRoadmap}>📄 View Roadmap</button>
            )}
            {hasRoadmap && (
              <button class="roads-action-btn" onClick={() => {
                window.dispatchEvent(new CustomEvent('valko-share', {
                  detail: { type: 'file', filePath: roadmap.roadmapRelPath, fileName: `${roadmap.projectKey}-ROADMAP.md`, pdfCapable: true, mediaEmbeddable: true, title: `${roadmap.projectKey} Roadmap` },
                }));
              }}>↗ Share Roadmap</button>
            )}
            <button class="roads-action-btn roads-action-btn--generate" onClick={generatePresentation}>
              ✨ Generate Deck
            </button>
          </div>

          {/* Presentations */}
          {hasPresentations && (
            <div class="roads-pres-list">
              <div class="roads-pres-section-label">Presentations</div>
              {roadmap.presentations.map((p) => (
                <PresentationCard key={p.path} pres={p} />
              ))}
            </div>
          )}

          {!hasRoadmap && !hasPresentations && (
            <div class="roads-card-empty">No roadmap or presentations yet</div>
          )}
        </div>
      )}
    </div>
  );
}

export function RoadsTab() {
  const [roadmaps, setRoadmaps] = useState<ProjectRoadmap[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedOnce = useRef(false);

  const fetchRoadmaps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/roads/list');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRoadmaps(Array.isArray(data) ? data : data.roadmaps ?? []);
    } catch (e: any) {
      setError(e.message || 'Failed to load roadmaps');
    } finally {
      setLoading(false);
    }
  }, []);

  if (!fetchedOnce.current) {
    fetchedOnce.current = true;
    fetchRoadmaps();
  }

  return (
    <div class="roads-tab">
      {/* Header */}
      <div class="roads-header">
        <div class="roads-header-left">
          <span class="roads-title">Roads</span>
          <span class="roads-subtitle">Roadmaps &amp; Investor Decks</span>
        </div>
        <button
          class={`roads-refresh-btn${loading ? ' roads-refresh-btn--spinning' : ''}`}
          onClick={fetchRoadmaps}
          disabled={loading}
        >↻</button>
      </div>

      {error && <div class="roads-error">{error}</div>}

      {!loading && !error && roadmaps.length === 0 && (
        <div class="roads-empty">
          <div class="roads-empty-icon">🗺️</div>
          <div class="roads-empty-title">No roadmaps found</div>
          <div class="roads-empty-hint">
            Add a <code>ROADMAP.md</code> to any project directory,<br />
            or generate a presentation deck via the Opus agent.
          </div>
        </div>
      )}

      <div class="roads-list">
        {roadmaps.map(r => (
          <RoadmapCard key={r.projectKey} roadmap={r} />
        ))}
      </div>
    </div>
  );
}
